// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  message?: string;
  metaData: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Request Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refreshToken?: string;
  user_id?: string;
  role?: string;
  permissions?: string[];
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    email: string;
    avatar?: string;
  };
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  password: string;
  token: string;
}

// User Types
export interface User {
  userId: string;
  uniqueId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  avatar?: string;
  phoneNumber?: string;
  isBlocked?: boolean;
  blockedAt?: string;
  status?: UserSearchStatus;
  createdAt?: string;
  updatedAt?: string;
  // Computed/helper properties
  fullName?: string;
}

// Lookup types for nested objects
export interface HeightLookup {
  height_id: string;
  height: string;
}

export interface ReligionLookup {
  religion_id: string;
  name: string;
  have_sect: boolean;
  have_caste: boolean;
  have_manglik: boolean;
}

export interface CasteLookup {
  caste_id: string;
  name: string;
}

export interface CountryLookup {
  country_id: string;
  name: string;
}

export interface StateLookup {
  state_id: string;
  name: string;
}

export interface CityLookup {
  city_id: string;
  name: string;
}

export interface MotherTongueLookup {
  mother_tongue_id: string;
  name: string;
}

export interface AnnualIncomeLookup {
  annual_income_id: string;
  name: string;
}

export interface MaritalStatusLookup {
  marital_status_id: string;
  name: string;
}

export interface DegreeLookup {
  degree_id: string;
  name: string;
}

export interface EmployedInLookup {
  employed_in_id: string;
  name: string;
}

export interface OccupationLookup {
  occupation_id: string;
  name: string;
}

export interface FamilyTypeLookup {
  family_type_id: string;
  name: string;
}

export interface FatherOccupationLookup {
  father_occupation_id: string;
  name: string;
}

export interface MotherOccupationLookup {
  mother_occupation_id: string;
  name: string;
}

// Generic lookup for backward compatibility
export interface LookupItem {
  name: string;
  [key: string]: any;
}

// Basic Details nested object
export interface UserBasicDetails {
  height?: HeightLookup | null;
  religion?: ReligionLookup | null;
  sect?: { sect_id: string; name: string } | null;
  jamat?: { jamat_id: string; name: string } | null;
  caste?: CasteLookup | null;
  country?: CountryLookup | null;
  state?: StateLookup | null;
  city?: CityLookup | null;
  motherTongue?: MotherTongueLookup | null;
  annualIncome?: AnnualIncomeLookup | null;
  marital_status?: MaritalStatusLookup | null;
  haveCasteBar?: boolean | null;
}

// Profile nested object
export interface UserProfile {
  haveCasteBar?: boolean | null;
  manglik?: string | null;
  isHoroscopeNecessary?: boolean | null;
  weight?: string | null;
  bodyType?: { body_type_id: string; name: string } | null;
  complexion?: { complexion_id: string; name: string } | null;
  bloodGroup?: { blood_group_id: string; name: string } | null;
  disability?: { disability_id: string; name: string } | null;
}

// Lifestyle nested object
export interface UserLifestyle {
  drinkingHabit?: { drinking_habit_id: string; name: string } | null;
  smokingHabit?: { smoking_habit_id: string; name: string } | null;
  diet?: { diet_id: string; name: string } | null;
  hobby?: string[];
}

// Education nested object
export interface IncomeCurrencyLookup {
  income_currency_id?: string;
  name?: string;
  symbol?: string;
  country?: string;
}

export interface UserEducation {
  highestDegree?: DegreeLookup | null;
  employedIn?: EmployedInLookup | null;
  occupation?: OccupationLookup | null;
  incomeCurrency?: IncomeCurrencyLookup | null;
  annualIncome?: number | null;
  companyName?: string | null;
  workLocation?: string | null;
}

// Family nested object
export interface UserFamily {
  familyType?: FamilyTypeLookup | null;
  fatherOccupation?: FatherOccupationLookup | null;
  motherOccupation?: MotherOccupationLookup | null;
  family_living_country?: CountryLookup | null;
  familyLivingState?: StateLookup | null;
  familyLivingCity?: CityLookup | null;
  address?: string | null;
  aboutFamily?: string | null;
  brothers?: string | null;
  brothers_married?: string | null;
  sisters?: string | null;
  sisters_married?: string | null;
}

// Active Plan Details
export interface ActivePlanDetails {
  hasActivePlan: boolean;
  planName: string | null;
  durationMonths: number | null;
  totalPrice: number | null;
  startDate: string | null;
  endDate: string | null;
}

// Extended User Detail - for single user view
export interface UserDetail extends User {
  emailVerified?: boolean;
  phoneVerified?: boolean;
  profileFor?: string;
  lastStep?: number;
  isProfileCompleted?: boolean;
  isCareerCompleted?: boolean;
  isLifestyleCompleted?: boolean;
  userImage?: string | null;
  profilePictureId?: number | null;
  aboutMe?: string | null;
  gender?: string;
  dateOfBirth?: string;
  isHiddenByAdmin?: boolean;
  
  // Nested objects
  basicDetails?: UserBasicDetails;
  profile?: UserProfile;
  lifestyle?: UserLifestyle;
  education?: UserEducation;
  family?: UserFamily;
  
  // Plan details
  activePlanDetails?: ActivePlanDetails;
  
  // Photos
  photos?: string[];
  otherImages?: UserOtherImage[];
}

export interface UserOtherImage {
  file_id: string;
  file_name: string;
  url: string;
}

export interface CreateUserRequest {
  // Personal Info (Required)
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profilePictureId?: number | null;
  otherPicturesIds?: number[];
  dateOfBirth: string;
  gender: string;
  
  // Religion & Caste (Required)
  motherTongueId: number;
  religionId: number;
  sectId?: number;
  jamatId?: number;
  casteId?: number;
  haveCasteBar?: boolean;
  
  // Profile Details (Required)
  manglik?: string;
  isHoroscopeNecessary?: boolean;
  maratialStatusId: number;
  heightId: number;
  
  // Physical Details (Optional)
  bodyTypeId?: number;
  weight?: number;
  complexionId?: number;
  bloodGroupId?: number;
  disabilityId?: number;
  
  // Location (Required)
  countryId: number;
  stateId: number;
  cityId: number;
  
  // Education & Career (Required)
  highestDegreeId: number;  
  employedInId: number;
  occupationId: number;
  income_currency_id: number;
  annualIncome: number;
  aboutYourself?: string;
  companyName?: string;
  workLocation?: string;
  
  // Lifestyle (Required)
  drinkingHabitId: number;
  smokingHabitId: number;
  dietId: number;
  hobbyIds?: number[];
  
  // Family (Optional)
  familyTypeId?: number;
  fatherOccupationId?: number;
  motherOccupationId?: number;
  brothers?: string;
  brothersMarried?: string;
  sisters?: string;
  sistersMarried?: string;
  familyLivingCountryId?: number;
  familyLivingStateId?: number;
  familyLivingCityId?: number;
  address?: string;
  aboutFamily?: string;
}

export interface UpdateUserRequest {
  // Personal Info
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  gender?: string;
  dateOfBirth?: string;
  aboutYourself?: string;
  
  // Profile Photos
  profilePictureId?: string | number | null;
  otherPicturesIds?: (string | number)[];
  
  // Religion & Community
  motherTongueId?: number;
  religionId?: number;
  sectId?: number;
  jamatId?: number;
  casteId?: number;
  haveCasteBar?: boolean;
  manglik?: string;
  isHoroscopeNecessary?: boolean;
  
  // Physical Profile
  maratialStatusId?: number;
  heightId?: number;
  bodyTypeId?: number;
  weight?: number;
  complexionId?: number;
  bloodGroupId?: number;
  disabilityId?: number;
  
  // Location
  countryId?: number;
  stateId?: number;
  cityId?: number;
  
  // Education & Career
  highestDegreeId?: number;
  employedInId?: number;
  occupationId?: number;
  income_currency_id?: number;
  annualIncome?: number;
  companyName?: string;
  workLocation?: string;
  
  // Lifestyle
  drinkingHabitId?: number;
  smokingHabitId?: number;
  dietId?: number;
  
  // Family
  familyTypeId?: number;
  fatherOccupationId?: number;
  motherOccupationId?: number;
  brothers?: string;
  brothersMarried?: string;
  sisters?: string;
  sistersMarried?: string;
  familyLivingCountryId?: number;
  familyLivingStateId?: number;
  familyLivingCityId?: number;
  address?: string;
  aboutFamily?: string;
}

// Plan Types
export interface PlanFeatures {
  advanceSearch: boolean;
  limitedSupport: boolean;
  limitedChat: boolean;
  highlightedProfile: boolean;
  astroMatchmaking: boolean;
  dedicatedManager: boolean;
  seniorManager: boolean;
  arrangedMeetings: boolean;
  hideProfile: boolean;
}

export interface PlanVariant {
  id: string;
  planId: string;
  duration: number; // in months
  price: number;
  connects: number;
  limitedChatDays: number | null;
  savings?: number; // percentage
  features: PlanFeatures;
  createdAt?: string;
  updatedAt?: string;
}

export type PlanCurrencyCode = 'INR' | 'USD' | 'CAD' | 'EUR';

export interface PlanRegionalPriceMap {
  INR: PriceValue;
  USD: PriceValue;
  CAD: PriceValue;
  EUR: PriceValue;
}

/** Plan list item from GET /admin/plans */
export interface AdminSubscriptionPlan {
  planId: string;
  name: string;
  description: string;
  isHidden: boolean;
  isFree: boolean;
  isDynamic: boolean;
  isPopular: boolean;
  createdAt?: string;
  updatedAt?: string;
  monthlyPrice: PlanRegionalPriceMap | null;
  yearlyPrice: PlanRegionalPriceMap | null;
  activeUserCount: number;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  color: string;
  isHidden: boolean;
  isFree: boolean;
  isPopular: boolean;
  variants: PlanVariant[];
  variantsCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePlanRequest {
  name: string;
  description: string;
  color?: string;
  isHidden?: boolean;
  isFree?: boolean;
  isPopular?: boolean;
}

export interface RegionalPrice {
  _e_u: PriceValue;
  _u_s: PriceValue;
  _i_n: PriceValue;
  _c_a: PriceValue;
}

export interface PriceValue {
  amount: number;
  taxPercent: number;
}

export interface ApiRegionalPrice {
  India?: PriceValue | number;
  US?: PriceValue | number;
  Canada?: PriceValue | number;
  EU?: PriceValue | number;
}

// Plan detail for assignment (from /admin/plan/all-details)
export interface PlanDetailForAssignment {
  planDetailId: string;
  durationMonths: number;
  price: {
    EU: PriceValue | number;
    US: PriceValue | number;
    India: PriceValue | number;
    Canada: PriceValue | number;
  };
}

export interface PlanDetailUpdate {
  planDetailId: number;
  price: RegionalPrice;
  connects: number;
  advanceSearch: boolean;
  limitedSupport: boolean;
  limitedChat: boolean;
  limitedChatDays: number;
  highlightedProfile: boolean;
  astroMatchmaking: boolean;
  dedicatedManager: boolean;
  dedicatedSeniorManager: boolean;
  arrangedMeetings: boolean;
  hideProfile: boolean;
}

export interface PlanDetailVariant {
  planDetailId: number;
  durationMonths: number;
  price: Record<string, unknown>;
  connects: number;
  advanceSearch: boolean;
  limitedSupport: boolean;
  limitedChat: boolean;
  limitedChatDays: number;
  highlightedProfile: boolean;
  astroMatchmaking: boolean;
  dedicatedManager: boolean;
  dedicatedSeniorManager?: boolean;
  arrangedMeetings: boolean;
  hideProfile: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlanDetailResponse {
  planId: string;
  name: string;
  description: string;
  isHidden: boolean;
  isFree: boolean;
  isDynamic: boolean;
  isPopular: boolean;
  createdAt?: string;
  updatedAt?: string;
  activeUserCount?: number;
  variants: PlanDetailVariant[];
}

export interface UpdatePlanRequest {
  name?: string;
  description?: string;
  color?: string;
  is_hidden?: boolean;
  is_free?: boolean;
  is_popular?: boolean;
  planDetails?: PlanDetailUpdate[];
}

export interface CreatePlanVariantRequest {
  planId: string;
  duration: number;
  price: number;
  connects: number;
  limitedChatDays?: number | null;
  savings?: number;
  features: PlanFeatures;
}

export interface UpdatePlanVariantRequest {
  duration?: number;
  price?: number;
  connects?: number;
  limitedChatDays?: number | null;
  savings?: number;
  features?: Partial<PlanFeatures>;
}

// User Plan Types
export interface UserPlan {
  user_plan_id?: number;
  user_id: string;
  unique_id: string | null;
  first_name: string;
  last_name: string;
  planId: string;
  name: string;
  duration_months: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  base_price: string;
  tax_price: string;
  total_price: string;
  currency?: string;
  connects: number;
  connects_used: number;
  note_from_admin?: string | null;
}

export interface UserPlansQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'expired' | 'cancelled';
  planId?: string;
  timePeriod?: 'last_7_days' | 'last_30_days' | 'last_90_days' | 'all_time';
  durationMonths?: 0 | 1 | 3 | 6 | 12;
}

export type UserSearchStatus =
  | 'active'
  | 'pending_verification'
  | 'inactive'
  | 'blocked'
  | 'invited';

export interface UserSearchApiRow {
  user_id: number | string;
  name: string;
  unique_id: string;
  email: string;
  phone_number: string;
  gender: string;
  age?: number | null;
  status: UserSearchStatus;
  is_paid: boolean;
  is_blocked?: boolean;
}

export interface UserListItem {
  userId: string;
  name: string;
  uniqueId: string;
  email: string;
  phoneNumber: string;
  gender: string;
  age: number | null;
  status: UserSearchStatus;
  isPaid: boolean;
  isBlocked: boolean;
}

export const mapUserSearchRow = (row: UserSearchApiRow): UserListItem => ({
  userId: String(row.user_id),
  name: row.name,
  uniqueId: row.unique_id,
  email: row.email ?? '',
  phoneNumber: row.phone_number ?? '',
  gender: row.gender ?? '',
  age: row.age ?? null,
  status: row.is_blocked ? 'blocked' : row.status,
  isPaid: Boolean(row.is_paid),
  isBlocked: Boolean(row.is_blocked),
});

export interface SearchUsersRequest {
  page?: number;
  limit?: number;
  search?: string;
  status?: UserSearchStatus;
  gender?: string;
  is_paid?: boolean;
  preferred_gender?: string;
  min_age?: number;
  max_age?: number;
  religion_id?: number;
  sect_id?: number;
  jamat_id?: number;
  caste_id?: number;
  have_caste_bar?: boolean;
  manglik_preference?: string;
  mother_tongue_id?: number;
  maratial_status_id?: number;
  marital_status_id?: number;
  min_height_id?: number;
  max_height_id?: number;
  country_id?: number;
  state_id?: number;
  city_id?: number;
  heighest_degree_id?: number;
  employed_in_id?: number;
  occupation_id?: number;
  income_currency_id?: number;
  min_annual_income?: number;
  max_annual_income?: number;
  min_weight?: number;
  max_weight?: number;
  complexion_id?: number;
  blood_group_id?: number;
  disability_id?: number;
  drinking_habit_id?: number;
  smoking_habit_id?: number;
  diet_id?: number;
  hobby_id?: number[];
}

export interface BulkExportUsersFilters {
  search?: string;
  status?: UserSearchStatus;
  gender?: string;
}

export interface BulkExportUsersRequest {
  userIds?: number[];
  filters?: BulkExportUsersFilters;
  format?: 'xlsx' | 'csv';
}

export interface BulkExportUsersResponse {
  exportedCount: number;
  filename: string;
  contentType: string;
  contentBase64?: string;
  csv?: string;
}

// Currency Types
export interface IncomeCurrency {
  income_currency_id: string;
  name: string;
  symbol: string;
  country: string;
}
